import React from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { UserAdminInfo, UserRevenuecatInfo, UserResource } from "../../api/users";
import { revenueCatCustomerUrl } from "../../api/constants";
import { useSnackbar } from "../../hooks/useSnackbar";

interface UserDetailDialogProps {
  open: boolean;
  user: UserAdminInfo | undefined;
  onClose: () => void;
}

type NullableBoolean = boolean | null | undefined;

function BooleanDisplay({ value }: { value: NullableBoolean }) {
  if (value === true) {
    return (
      <Typography component="span" sx={{ color: "success.main", fontWeight: 500 }}>
        ✓
      </Typography>
    );
  }
  if (value === false) {
    return (
      <Typography component="span" sx={{ color: "error.main", fontWeight: 500 }}>
        ✗
      </Typography>
    );
  }
  return (
    <Typography component="span" sx={{ color: "text.disabled" }}>
      —
    </Typography>
  );
}

export const UserDetailDialog: React.FC<UserDetailDialogProps> = ({
  open,
  user,
  onClose,
}) => {
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const [rcInfo, setRcInfo] = React.useState<UserRevenuecatInfo | null>(null);
  const [rcLoading, setRcLoading] = React.useState(false);

  React.useEffect(() => {
    if (open && user?.id) {
      setRcInfo(null);
      setRcLoading(true);
      UserResource.getRevenuecat(user.id)
        .then(setRcInfo)
        .catch(() => setRcInfo(null))
        .finally(() => setRcLoading(false));
    }
  }, [open, user?.id]);

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    showSnackbar("Copied!");
  };

  if (!user) return null;

  const rcUrl = user.revenueCatId ? revenueCatCustomerUrl(user.revenueCatId) : undefined;
  const isConfirmed = user.accountState === "CONFIRMED";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            component="span"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {user.email}
          </Typography>
          <Chip
            label={user.accountState}
            size="small"
            sx={{
              backgroundColor: isConfirmed ? "success.light" : "error.light",
              color: isConfirmed ? "success.dark" : "error.dark",
              fontWeight: 600,
              flexShrink: 0,
            }}
          />
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Identity section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Identity
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ width: 140, flexShrink: 0, color: "text.secondary" }}>
                Display name
              </Typography>
              <Typography variant="body2">{user.displayName || "—"}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ width: 140, flexShrink: 0, color: "text.secondary" }}>
                Created at
              </Typography>
              <Typography variant="body2">
                {user.createdAtUtc ? new Date(user.createdAtUtc).toLocaleString() : "—"}
              </Typography>
            </Box>
            <TextField
              label="Cognito ID"
              value={user.id}
              fullWidth
              size="small"
              inputProps={{ readOnly: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy">
                      <IconButton onClick={() => handleCopy(user.id)} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            {user.fsId && (
              <TextField
                label="App User ID (fs_id)"
                value={user.fsId}
                fullWidth
                size="small"
                inputProps={{ readOnly: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <IconButton onClick={() => handleCopy(user.fsId!)} edge="end" size="small">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </Box>

        {/* Billing section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Billing
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              label="RevenueCat ID"
              value={user.revenueCatId ?? "—"}
              fullWidth
              size="small"
              inputProps={{ readOnly: true }}
              InputProps={{
                endAdornment: user.revenueCatId ? (
                  <InputAdornment position="end">
                    <Tooltip title="Copy">
                      <IconButton
                        onClick={() => handleCopy(user.revenueCatId!)}
                        edge="end"
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {rcUrl && (
                      <Tooltip title="Open in RevenueCat">
                        <IconButton
                          component="a"
                          href={rcUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          edge="end"
                          size="small"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>
        </Box>

        {/* Settings section */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Settings
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ flex: 1, color: "text.secondary" }}>
                Notifications allowed
              </Typography>
              <BooleanDisplay value={user.notificationsAllow as NullableBoolean} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ flex: 1, color: "text.secondary" }}>
                Weekly emails enabled
              </Typography>
              <BooleanDisplay value={user.settingWeeklyEmailsEnabled as NullableBoolean} />
            </Box>
          </Box>
        </Box>

        {/* Subscription section */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Subscription (RevenueCat)
          </Typography>
          {rcLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!rcLoading && rcInfo?.subscriber === null && (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              No RevenueCat record
            </Typography>
          )}
          {!rcLoading && rcInfo?.subscriber && (() => {
            const { subscriber } = rcInfo;
            const entitlementEntries = Object.entries(subscriber.entitlements);
            const subscriptionEntries = Object.entries(subscriber.subscriptions);
            const now = new Date();
            const activeEntitlements = entitlementEntries.filter(
              ([, e]) => !e.expires_date || new Date(e.expires_date) > now
            );
            const hasGracePeriod = subscriptionEntries.some(
              ([, s]) => s.grace_period_expires_date && new Date(s.grace_period_expires_date) > now
            );
            const activePeriodTypes = activeEntitlements.flatMap(([, e]) =>
              subscriptionEntries
                .filter(([pid]) => pid === e.product_identifier)
                .map(([, s]) => s.period_type)
            );
            const getStatus = () => {
              if (entitlementEntries.length === 0) return "no_subscription";
              if (activeEntitlements.length === 0) return "expired";
              if (hasGracePeriod) return "grace_period";
              if (activePeriodTypes.includes("trial")) return "trial";
              if (activePeriodTypes.includes("intro")) return "intro";
              return "active";
            };
            const status = getStatus();
            const statusConfig: Record<string, { label: string; bg: string; color: string }> = {
              active:          { label: "Active",       bg: "success.light", color: "success.dark" },
              trial:           { label: "Trial",        bg: "#fff3e0",       color: "#e65100" },
              intro:           { label: "Intro",        bg: "#e3f2fd",       color: "#0d47a1" },
              grace_period:    { label: "Grace Period", bg: "#fce4ec",       color: "#880e4f" },
              expired:         { label: "Expired",      bg: "error.light",   color: "error.dark" },
              no_subscription: { label: "No subscription", bg: "grey.200",  color: "text.secondary" },
            };
            const sc = statusConfig[status];
            const stores = subscriptionEntries.map(([, s]) => s.store).filter((s, i, arr) => arr.indexOf(s) === i);
            const storeLabels: Record<string, string> = {
              app_store: "App Store (iOS)",
              play_store: "Play Store (Android)",
              stripe: "Stripe (Web)",
              promotional: "Promotional",
              amazon: "Amazon",
              roku: "Roku",
              paddle: "Paddle",
            };
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ width: 140, flexShrink: 0, color: "text.secondary" }}>
                    Status
                  </Typography>
                  <Chip
                    label={sc.label}
                    size="small"
                    sx={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 600 }}
                  />
                </Box>
                {stores.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ width: 140, flexShrink: 0, color: "text.secondary" }}>
                      Provider
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {stores.map((store) => (
                        <Chip
                          key={store}
                          label={storeLabels[store] ?? store}
                          size="small"
                          sx={{ fontSize: "0.75rem" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {entitlementEntries.map(([key, entitlement]) => (
                  <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ width: 140, flexShrink: 0, color: "text.secondary" }}>
                      {key}
                    </Typography>
                    <Typography variant="body2">
                      {entitlement.product_identifier}
                      {entitlement.expires_date && (
                        <Typography component="span" variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
                          · expires {new Date(entitlement.expires_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                ))}
                {subscriber.first_seen && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ width: 140, flexShrink: 0, color: "text.secondary" }}>
                      First seen
                    </Typography>
                    <Typography variant="body2">
                      {new Date(subscriber.first_seen).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {SnackbarNode}
    </Dialog>
  );
};
